<template>
  <div style="position: relative;">
    <div
      v-if="showSpinner || showSpinnerOnUpdate"
      class="spinner-overlay"
    >
      <v-icon
        size="48"
        class="mdi-spin"
      >
        mdi-loading
      </v-icon>
    </div>
    <div
      ref="chartContainer"
      style="width: 100%;"
      :class="{ 'chart-container-dashboard': forDashboard && !statMode, 'chart-container-stat': statMode }"
    >
      <div class="graphic-title">
        {{ graphic.name }}
      </div>
      <div class="button-container">
        <!-- LEFT BUTTON GROUP -->
        <div class="button-group-left">
          <DownloadSelect
            v-if="!forDashboard && !workOrderGraph"
            class="item"
            :graphicsWrapper="$refs.graphicsWrapper ? $refs.graphicsWrapper : {}"
            :seriesData="seriesData"
            :series="series"
            :svgRef="$refs.lineChart ? $refs.lineChart : {}"
          />
          <button
            v-if="!forDashboard && !workOrderGraph"
            :class="{ active: mode === 'pan' }"
            class="toggle-button item"
            @click="toggleMode"
          >
            🖐️{{ $t('graphics.panMode') }}
          </button>
          <div
            v-if="!forDashboard"
          >
            <!-- Toggle Button -->
            <!-- Annotations hidden for the release of July 2025 will be added later -->
            <v-btn
              v-if="!workOrderGraph"
              class="toggle-btn"
              @click="toggleButtons"
            >
              <v-icon>{{ expanded ? 'mdi-chevron-left' : 'mdi-chevron-right' }}</v-icon>
            </v-btn>

            <!-- Animated Container -->
            <div
              class="buttons-wrapper"
              :class="{ expanded }"
            >
              <v-slide-x-transition>
                <v-btn
                  v-if="expanded"
                  @click="addAnnotations('circle')"
                >
                  <v-icon>mdi-circle-edit-outline</v-icon>
                </v-btn>
              </v-slide-x-transition>

              <v-slide-x-transition>
                <v-btn
                  v-if="expanded"
                  @click="addAnnotations('rectangle')"
                >
                  <v-icon>mdi-square-edit-outline</v-icon>
                </v-btn>
              </v-slide-x-transition>

              <v-slide-x-transition>
                <v-btn
                  v-if="expanded"
                  @click="addAnnotations()"
                >
                  <v-icon>mdi-minus</v-icon>
                </v-btn>
              </v-slide-x-transition>
              <v-slide-x-transition>
                <v-tooltip
                  v-if="expanded"
                  bottom
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      :color="showMinMaxLines ? 'primary' : ''"
                      v-bind="attrs"
                      v-on="on"
                      @click="showMinMaxLines = !showMinMaxLines"
                    >
                      <v-icon>mdi-swap-vertical</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('graphics.showMinMax') }}</span>
                </v-tooltip>
              </v-slide-x-transition>
            </div>
          </div>
          <div>
            <v-btn
              v-if="showEdit"
              class="ma-2"
              dark
              :style="{ backgroundColor: 'green' }"
              @click="confirmModification"
            >
              <v-icon dark>
                check
              </v-icon>
            </v-btn>
            <v-btn
              v-if="showEdit"
              class="ma-2"
              dark
              :style="{ backgroundColor: 'red' }"
              @click="deleteAnnotation"
            >
              <v-icon dark>
                delete
              </v-icon>
            </v-btn>
          </div>
        </div>
        <!-- END OF LEFT BUTTON GROUP -->

        <!-- RIGHT BUTTON GROUP -->
        <div
          v-if="!statMode"
          :class="['item', { 'button-container-dashboard': forDashboard }]"
        >
          <label
            v-for="(item, index) in analysisConfig"
            :key="index"
            :style="{ color: colorScale(index) }"
          >
            <input
              v-model="lineVisibility[index]"
              type="checkbox"
              class="checkbox-style"
            >
            {{ item.analysisName }} ({{ item.unit }})
          </label>
        </div>
        <!-- END OF RIGHT BUTTON GROUP -->
      </div>
      <!-- END OF TOP GROUP -->
      <div
        ref="graphicsWrapper"
        style="width: 100%; height: 100%;"
      >
        <svg
          v-if="!statMode"
          ref="lineChart"
          style="margin-bottom: 10px;"
        ><!-- SVG will be drawn here --></svg>
        <v-carousel
          v-if="(!forDashboard && !workOrderGraph) || statMode"
          v-model="activeSlide"
          hideDelimiterBackground
          :height="forDashboard ? '100%':300"
          showArrowsOnHover
          hideDelimiters
          :light="true"
        >
          <v-carousel-item>
            <Statistics
              :seriesData="seriesData"
              :series="analysisConfig"
              :colorScale="colorScale"
            />
          </v-carousel-item>
          <v-carousel-item>
            <D3DonutChart :data="seriesData" />
          </v-carousel-item>
          <v-carousel-item>
            <D3StackedBarChart :data="seriesData" />
          </v-carousel-item>
        </v-carousel>
      </div>
      <Tooltip
        :content="tooltipContent"
        :position="tooltipPosition"
        :visible="showTooltip"
      />
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import * as d3Annotation from 'd3-svg-annotation';
import Statistics from '@/components/graphics/Statistics';
import createColorScale from '@/components/graphics/colorScale';
import DownloadSelect from '@/components/graphics/downloadSelect';
import Tooltip from '@/components/graphics/Tooltip';
import { timeFormatLocale } from 'd3-time-format';
import { enLocaleData, frLocaleData } from '@/components/graphics/chartOptions';
import { mapGetters } from 'vuex';
import Vue from 'vue';
import { getLegendMetadata } from '@/utilities/graphs';
import ColorPicker from '../shared/ColorPicker.vue';
import D3DonutChart from './D3DonutChart.vue';
import D3StackedBarChart from './D3StackedBarChart.vue';

export default {
    name: 'D3LineChart',
    components: {
        Statistics,
        DownloadSelect,
        Tooltip,
        D3DonutChart,
        D3StackedBarChart,
    },
    props: {
        seriesData: {
            type: Array,
            default: () => [],
        },
        series: {
            type: Array,
            default: () => [],
        },
        forDashboard: {
            type: Boolean,
            default: false,
        },
        statMode: {
            type: Boolean,
            default: false,
        },
        showSpinner: {
            type: Boolean,
            default: false,
        },
        showSpinnerOnUpdate: {
            type: Boolean,
            default: false,
        },
        graphic: {
            type: Object,
            default: () => ({}),
        },
        workOrderGraph: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            margin: { top: 20, right: 150, bottom: 100, left: 150 },
            dataSeries: [],
            activeSlide: 0,
            lineWidth: 2.5,
            mode: this.workOrderGraph ? 'pan' : 'brush',
            svg: null,
            lineGroup: null,
            x: null,
            yScales: [],
            xAxisGroup: null,
            xGridGroup: null,
            showMinMaxLines: false,
            yAxisGroups: [],
            selectedAnnotation: null,
            yGridGroup: null,
            showEdit: false,
            lineGenerators: [],
            zoom: null,
            brush: null,
            colorScale: createColorScale(),
            annotations: [],
            idleTimeout: null,
            lineVisibility: [],
            tooltip: null,
            verticalLine: null,
            currentTransform: d3.zoomIdentity,
            minMaxValues: [],
            tooltipContent: '',
            tooltipPosition: { x: 0, y: 0 },
            showTooltip: false,
            transitionTime: 1000,
            locale: this.$store.getters.lang,
            formatTooltipDate: null,
            graphWidth: 0,
            tickSettings: [],
            color: '#007bff',
            expanded: false,
            chartWidth: 0,
            chartHeight: 0,
            analysisConfig: [],
        };
    },
    computed: {
        ...mapGetters(['lang', 'teamMainLang']),
    },
    watch: {
        showMinMaxLines(newVal) {
            this.toggleMinMaxLines(newVal);
        },
        seriesData: {
            deep: true,
            immediate: true,
            handler(value) {
                this.dataSeries = value.map(series => series.data);
                if (this.svg) {
                    d3.select(this.$refs.lineChart).selectAll('*').remove();
                    this.svg = null;
                    this.lineGroup = null;
                    this.xAxisGroup = null;
                    this.xGridGroup = null;
                    this.yAxisGroups = [];
                    this.yGridGroup = null;
                    this.lineGenerators = [];
                    this.currentTransform = d3.zoomIdentity;
                }
                if (this.dataSeries.length) {
                    this.$nextTick(() => {
                        this.lineVisibility = this.dataSeries.map(() => true);
                        this.drawChart();
                    });
                }
            },
        },
        lineVisibility: {
            handler() {
                if (this.$refs.chartContainer.clientWidth && this.$refs.chartContainer.clientHeight) {
                    this.updateLineVisibility();
                }
            },
            deep: true,
        },
        series: {
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.getConfig(newVal);
                }
            },
            deep: true,
        },
    },
    mounted() {
        window.addEventListener('resize', this.onResize);
        this.$nextTick(() => {
            const resizeObserver = new ResizeObserver(() => {
                this.onResize();
            });
            resizeObserver.observe(this.$refs.chartContainer);
        });
        this.getConfig(this.series);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.onResize);
    },
    methods: {
        async getConfig(series) {
            this.analysisConfig = await getLegendMetadata(this.$rq, series, this.lang, this.teamMainLang);
        },
        toggleMinMaxLines(visible, newScale) {
            if (!this.lineGroup) return;

            this.lineGroup.selectAll('.min-max-line').remove();

            if (visible) {
                this.dataSeries.forEach((series, index) => {
                    if (!this.lineVisibility[index]) return;
                    const yScale = newScale ? newScale[index] : this.yScales[index];
                    const color = this.colorScale(index);
                    const minPoint = series.findLast(point => point.metadata?.norm?.minimum !== undefined);
                    const maxPoint = series.findLast(point => point.metadata?.norm?.maximum !== undefined);
                    const minVal = minPoint?.metadata?.norm?.minimum;
                    const maxVal = maxPoint?.metadata?.norm?.maximum;
                    if (minVal !== undefined) {
                        this.lineGroup.append('line')
                            .attr('class', 'min-max-line')
                            .attr('x1', 0)
                            .attr('x2', this.graphWidth)
                            .attr('y1', yScale(minVal))
                            .attr('y2', yScale(minVal))
                            .attr('stroke', color)
                            .attr('stroke-width', 1)
                            .attr('stroke-dasharray', '5,5');
                    }
                    if (maxVal !== undefined) {
                        this.lineGroup.append('line')
                            .attr('class', 'min-max-line')
                            .attr('x1', 0)
                            .attr('x2', this.graphWidth)
                            .attr('y1', yScale(maxVal))
                            .attr('y2', yScale(maxVal))
                            .attr('stroke', color)
                            .attr('stroke-width', 1)
                            .attr('stroke-dasharray', '5,5');
                    }
                });
            }
        },

        toggleButtons() {
            this.expanded = !this.expanded;
        },
        confirmModification() {
            if (this.selectedAnnotation) {
                this.selectedAnnotation.makeAnnotations.editMode(false).update();
                this.selectedAnnotation = null;
                this.showEdit = false;
            }
        },
        deleteAnnotation() {
            if (this.selectedAnnotation) {
                this.selectedAnnotation.group.remove();
                this.annotations = this.annotations.filter(
                    annotation => annotation.id !== this.selectedAnnotation.id,
                );
                this.selectedAnnotation = null;
                this.showEdit = false;
            }
        },
        onResize() {
            d3.select(this.$refs.lineChart).selectAll('*').remove();
            if (this.svg) {
                this.svg = null;
                this.drawChart();
            }
        },
        toggleMode() {
            this.mode = this.mode === 'brush' ? 'pan' : 'brush';
            this.updateInteractions();
        },
        updateInteractions() {
            this.svg.on('.zoom', null);
            this.lineGroup.select('.brush').remove();

            if (this.mode === 'pan') {
                this.svg.call(this.zoom).on('dblclick.zoom', null);
                this.lineGroup.select('rect').attr('pointer-events', 'all');
            } else {
                this.svg.on('.zoom', null);
                if (!this.forDashboard) {
                    this.lineGroup.append('g').attr('class', 'brush').call(this.brush);
                }
                this.lineGroup.select('rect').attr('pointer-events', 'none');
            }
        },
        drawChart() {
            const { margin } = this;
            if (!this?.$refs?.chartContainer?.clientWidth || !this?.$refs?.chartContainer?.clientHeight) return;
            let width = this.$refs.chartContainer.clientWidth - margin.left - margin.right;
            let height = (this.$refs.chartContainer.clientHeight - margin.top - margin.bottom) * 0.85;
            if (width < 0) width = this.$refs.chartContainer.clientWidth || 0;
            if (height < 0) height = this.$refs.chartContainer.clientHeight || 0;

            this.chartWidth = width;
            if (!this.forDashboard) {
                height = height > 400 ? 400 : height;
            }
            this.chartHeight = height;
            this.graphWidth = width;
            if (!this.svg) {
                this.svg = d3
                    .select(this.$refs.lineChart)
                    .attr('viewBox', `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
                    .style('width', '100%')
                    .style('height', 'auto');

                const g = this.svg
                    .append('g')
                    .attr('transform', `translate(${margin.left},${margin.top})`);

                const allDates = [].concat(
                    ...this.dataSeries.map(series => series.map(d => d.date)),
                );

                this.x = d3
                    .scaleTime()
                    .domain(d3.extent(allDates))
                    .range([0, width]);

                this.yScales = this.dataSeries.map(series => d3
                    .scaleLinear()
                    .domain([
                        d3.min(series, d => d.value)
                  - d3.max(series, d => d.value) * 0.1,
                        d3.max(series, d => d.value)
                  + d3.max(series, d => d.value) * 0.1,
                    ])
                    .range([height, 0]));

                let localeData;
                if (this.locale.startsWith('fr')) {
                    localeData = frLocaleData;
                } else {
                    localeData = enLocaleData;
                }
                const locale = timeFormatLocale(localeData);

                this.tickSettings = [
                    {
                        threshold: 7 * 24 * 60 * 60 * 1000,
                        tickInterval: d3.timeWeek.every(1),
                        tickFormat: locale.format('%b %d %Y'),
                    },
                    {
                        threshold: 24 * 60 * 60 * 1000,
                        tickInterval: d3.timeHour.every(12),
                        tickFormat: locale.format('%a %d %H:%M'),
                    },
                    {
                        threshold: 0,
                        tickInterval: d3.timeHour.every(1),
                        tickFormat: locale.format('%H:%M'),
                    },
                ];

                this.formatTooltipDate = locale.format('%A, %d %B %Y');

                const { interval, format } = this.getTickSettings();

                this.xAxisGroup = g
                    .append('g')
                    .attr('transform', `translate(0,${height})`)
                    .call(d3.axisBottom(this.x).ticks(interval).tickFormat(format));

                // Rotate x-axis tick labels vertically
                this.xAxisGroup.selectAll('text')
                    .attr('transform', 'rotate(-90)')
                    .style('text-anchor', 'end')
                    .attr('dx', '-0.8em')
                    .attr('dy', '-0.4em');

                this.applyXAxisStyles();

                this.xGridGroup = g
                    .append('g')
                    .attr('class', 'grid x-grid')
                    .attr('transform', `translate(0,${height})`)
                    .call(
                        d3.axisBottom(this.x)
                            .ticks(interval)
                            .tickSize(-height)
                            .tickFormat(''),
                    );

                this.applyGridStyles(this.xGridGroup);
                const xDomain = this.x.domain();
                const minDate = xDomain[0];
                const maxDate = xDomain[1];

                g.append('line')
                    .attr('class', 'vertical-gridline')
                    .datum(minDate)
                    .attr('x1', this.x(minDate))
                    .attr('x2', this.x(minDate))
                    .attr('y1', 0)
                    .attr('y2', height)
                    .style('stroke', '#e0e0e0')
                    .style('stroke-opacity', 0.7)
                    .style('shape-rendering', 'crispEdges');

                g.append('line')
                    .attr('class', 'vertical-gridline')
                    .datum(maxDate)
                    .attr('x1', this.x(maxDate))
                    .attr('x2', this.x(maxDate))
                    .attr('y1', 0)
                    .attr('y2', height)
                    .style('stroke', '#e0e0e0')
                    .style('stroke-opacity', 0.7)
                    .style('shape-rendering', 'crispEdges');
                this.yAxisGroups = [];
                this.lineGenerators = [];
                this.dataSeries.forEach((series, index) => {
                    const color = this.colorScale(index);
                    const yAxisPosition = index % 2 === 0 ? -index * 35 : width + (index - 1) * 35;

                    const yAxis = index % 2 === 0
                        ? d3.axisLeft(this.yScales[index])
                        : d3.axisRight(this.yScales[index]);
                    const yAxisGroup = g
                        .append('g')
                        .attr('class', `y-axis y-axis${index}`)
                        .attr('transform', `translate(${yAxisPosition},0)`)
                        .call(yAxis)
                        .style('fill', color)
                        .style('color', color);

                    yAxisGroup.select('.domain').remove();

                    yAxisGroup
                        .selectAll('.tick line')
                        .attr('stroke', color)
                        .attr('stroke-width', 1)
                        .attr('opacity', 0.8)
                        .attr('padding-top', '10px');

                    yAxisGroup
                        .selectAll('.tick text')
                        .attr('fill', color)
                        .style('font-size', '10px')
                        .style('font-weight', 'bold');

                    this.yAxisGroups.push(yAxisGroup);

                    const lineGenerator = d3
                        .line()
                        .x(d => this.x(d.date))
                        .y(d => this.yScales[index](d.value));

                    this.lineGenerators.push(lineGenerator);

                    if (index === 0) {
                        // eslint-disable-next-line no-underscore-dangle
                        const clipId = `clip${this._uid}`;
                        this.svg
                            .append('defs')
                            .append('clipPath')
                            .attr('id', clipId)
                            .append('rect')
                            .attr('width', width)
                            .attr('height', height)
                            .attr('x', 0)
                            .attr('y', 0);

                        this.lineGroup = this.svg
                            .append('g')
                            .attr('clip-path', `url(#${clipId})`)
                            .attr('transform', `translate(${margin.left},${margin.top})`);
                    }

                    this.lineGroup
                        .append('path')
                        .datum(series)
                        .attr('class', `line line${index}`)
                        .attr('fill', 'none')
                        .attr('stroke', color)
                        .attr('stroke-width', this.lineWidth)
                        .attr('d', lineGenerator);
                });

                this.yGridGroup = g
                    .append('g')
                    .attr('class', 'grid y-grid')
                    .call(
                        d3.axisLeft(this.yScales[0])
                            .ticks(10)
                            .tickSize(-width)
                            .tickFormat('')
                            .tickPadding(25),
                    );

                this.applyGridStyles(this.yGridGroup);

                this.minMaxValues = this.dataSeries.map(series => ({
                    minValue: d3.min(series, d => d.value),
                    maxValue: d3.max(series, d => d.value),
                    unit: series[0]?.metadata?.unit || '',
                }));

                this.verticalLine = this.lineGroup
                    .append('line')
                    .attr('class', 'vertical-line')
                    .attr('stroke', 'black')
                    .attr('stroke-width', 1)
                    .attr('stroke-dasharray', '3,3')
                    .attr('opacity', 0);

                this.lineGroup
                    .append('rect')
                    .attr('width', width)
                    .attr('height', height)
                    .attr('fill', 'none')
                    .attr('pointer-events', 'all')
                    .on('mouseover', () => {
                        this.verticalLine.attr('opacity', 1);
                        this.showTooltip = true;
                    })
                    .on('mouseout', () => {
                        this.verticalLine.attr('opacity', 0);
                        this.showTooltip = false;
                    })
                    .on('mousemove', event => this.handleMouseMove(event, width, height));

                this.zoom = d3
                    .zoom()
                    .scaleExtent([0.5, 100])
                    .extent([
                        [0, 0],
                        [width, height],
                    ])
                    .on('zoom', this.zoomed);
                if (!this.forDashboard) {
                    this.brush = d3
                        .brush()
                        .extent([
                            [0, 0],
                            [width, height],
                        ])
                        .on('end', this.updateChart);
                }
                this.updateInteractions();

                this.svg.on('dblclick', () => {
                    this.resetZoom();
                });
                this.updateLineVisibility();
            }
            // Save graph on work order graph part
            this.$emit('updatedOpenedGraphs', { graphicId: this.graphic.id, graph: this?.$refs?.lineChart, graphName: this.graphic.name });
        },
        addAnnotations(type) {
            if (this.workOrderGraph) {
                this.createAnnotation('#27384b', type);
            } else {
                this.$root.$emit('showModal', {
                    component: Vue.component('colorPicker', ColorPicker),
                    width: 500,
                    onConfirm: color => this.createAnnotation(color, type),
                    isForm: false,
                });
            }
        },
        createAnnotation(color, type) {
            const black = '#000000';
            let annotationType;
            let newAnnotations = {};
            switch (type) {
                case 'circle':
                    annotationType = d3Annotation.annotationCalloutCircle;
                    newAnnotations = {
                        id: this.annotations.length,
                        note: {
                            label: this.$t('graphics.description'),
                            title: this.$t('graphics.title'),
                            wrap: 300,
                        },
                        dy: -50,
                        dx: 50,
                        x: 200,
                        y: 200,
                        connector: {
                            end: 'dot',
                            type: 'line',
                            strokeWidth: 2,
                        },
                        subject: {
                            radius: 40,
                            radiusPadding: 5,
                        },
                        color: color || black,
                    };
                    break;
                case 'rectangle':
                    annotationType = d3Annotation.annotationCalloutRect;
                    newAnnotations = {
                        id: this.annotations.length,
                        note: {
                            label: this.$t('graphics.description'),
                            title: this.$t('graphics.title'),
                            wrap: 300,
                        },
                        dy: -50,
                        dx: 50,
                        x: 200,
                        y: 200,
                        subject: {
                            width: 100,
                            height: 100,
                        },
                        color: color || black,
                    };
                    break;
                default:
                    annotationType = d3Annotation.annotationXYThreshold;
                    newAnnotations = {
                        id: this.annotations.length,
                        note: {
                            label: this.$t('graphics.description'),
                            title: this.$t('graphics.title'),
                            wrap: 300,
                        },
                        dy: -50,
                        dx: 50,
                        x: 200,
                        y: 200,
                        subject: {
                            x1: this.margin.left,
                            x2: this.graphWidth + this.margin.left,
                        },
                        color: color || black,

                    };
                    break;
            }
            this.annotations.push(newAnnotations);

            const makeAnnotations = d3Annotation.annotation()
                .editMode(true)
                .notePadding(15)
                .type(annotationType)
                .accessors({
                    x: d => (d.x !== undefined ? d.x : 200),
                    y: d => (d.y !== undefined ? d.y : 200),
                })
                .annotations([newAnnotations]);
            const annotationGroup = this.svg.append('g')
                .attr('class', 'annotation-group')
                .call(makeAnnotations)
                .on('click', () => {
                    this.selectedAnnotation = {
                        id: newAnnotations.id,
                        group: annotationGroup,
                        makeAnnotations,
                    };
                    makeAnnotations.editMode(true).update();
                    this.showEdit = true;
                });
            makeAnnotations.on('dragend', d => {
                const annotation = this.annotations.find(a => a.id === d.id);
                if (annotation) {
                    annotation.x = d.x;
                    annotation.y = d.y;
                }
            });
            this.bindAnnotationEditing(annotationGroup, makeAnnotations);
            this.selectedAnnotation = {
                id: newAnnotations.id,
                group: annotationGroup,
                makeAnnotations,
            };
            this.showEdit = true;
        },
        bindAnnotationEditing(annotationGroup, makeAnnotations) {
            annotationGroup.selectAll('.annotation-note-title')
                .on('dblclick', (event, d) => {
                    const noteEl = d3.select(event.currentTarget);
                    noteEl.style('visibility', 'hidden');
                    const bbox = event.currentTarget.getBBox();

                    const fo = d3.select(event.currentTarget.parentNode)
                        .append('foreignObject')
                        .attr('x', bbox.x)
                        .attr('y', bbox.y)
                        .attr('width', bbox.width + 20)
                        .attr('height', bbox.height + 20);

                    const editableDiv = fo.append('xhtml:div')
                        .attr('contenteditable', true)
                        .style('width', '100%')
                        .style('height', '100%')
                        .style('font-size', noteEl.style('font-size'))
                        .style('font-weight', noteEl.style('font-weight'))
                        .style('color', noteEl.style('fill'))
                        .style('background', 'transparent')
                        .style('outline', 'none')
                        .html(d.note.title)
                        .node();

                    editableDiv.focus();

                    editableDiv.addEventListener('blur', () => {
                        const updatedNote = { ...d.note, title: editableDiv.textContent };
                        // eslint-disable-next-line no-param-reassign
                        d.note = updatedNote;
                        fo.remove();
                        noteEl.style('visibility', 'visible');
                        makeAnnotations.updateText();
                        makeAnnotations.update();
                    });
                });

            annotationGroup.selectAll('.annotation-note-label')
                .on('dblclick', (event, d) => {
                    const noteEl = d3.select(event.currentTarget);
                    noteEl.style('visibility', 'hidden');
                    const bbox = event.currentTarget.getBBox();

                    const fo = d3.select(event.currentTarget.parentNode)
                        .append('foreignObject')
                        .attr('x', bbox.x)
                        .attr('y', bbox.y)
                        .attr('width', bbox.width + 20)
                        .attr('height', bbox.height + 20);

                    const editableDiv = fo.append('xhtml:div')
                        .attr('contenteditable', true)
                        .style('width', '100%')
                        .style('height', '100%')
                        .style('font-size', noteEl.style('font-size'))
                        .style('font-weight', noteEl.style('font-weight'))
                        .style('color', noteEl.style('fill'))
                        .style('background', 'transparent')
                        .style('outline', 'none')
                        .html(d.note.label)
                        .node();

                    editableDiv.focus();

                    editableDiv.addEventListener('blur', () => {
                        // eslint-disable-next-line no-param-reassign
                        d.note.label = editableDiv.textContent;
                        fo.remove();
                        noteEl.style('visibility', 'visible');
                        makeAnnotations.updateText();
                        makeAnnotations.update();
                    });
                });
        },
        handleMouseMove(event, width, height) {
            const [mouseX] = d3.pointer(event, this.lineGroup.node());
            const transform = this.currentTransform || d3.zoomIdentity;
            const newX = transform.rescaleX(this.x);
            const x0 = newX.invert(mouseX);

            this.verticalLine
                .attr('x1', mouseX)
                .attr('x2', mouseX)
                .attr('y1', 0)
                .attr('y2', height);

            const valuesAtX = [];
            const pixelThreshold = 5;

            this.dataSeries.forEach((series, index) => {
                if (!this.lineVisibility[index]) return;
                const bisectDate = d3.bisector(d => d.date).left;
                const idx = bisectDate(series, x0);

                let d;
                let minDist = Infinity;

                [series[idx - 1], series[idx]].forEach(candidate => {
                    if (candidate) {
                        const xPos = newX(candidate.date);
                        const dist = Math.abs(xPos - mouseX);
                        if (dist < minDist && dist < pixelThreshold) {
                            minDist = dist;
                            d = candidate;
                        }
                    }
                });

                if (d) {
                    const yValue = d.value;
                    const { unit } = this.minMaxValues[index];
                    const normMin = d.metadata?.norm?.minimum ?? this.$t('graphics.noMinNorm');
                    const normMax = d.metadata?.norm?.maximum ?? this.$t('graphics.noMaxNorm');
                    const formattedDate = this.formatTooltipDate(d.date);
                    valuesAtX.push({
                        name: this.series[index].analysisName || `${this.$t('graphics.labels.line')} ${index + 1}`,
                        componentName: this.series[index].componentName || `${this.$t('graphics.labels.line')} ${index + 1}`,
                        yValue,
                        normMin,
                        normMax,
                        color: this.colorScale(index),
                        unit,
                        formattedDate,
                    });
                }
            });

            if (valuesAtX.length > 0) {
                let tooltipContent = '';
                valuesAtX.forEach(value => {
                    tooltipContent += '<div style="color: #fff;">';
                    tooltipContent += `<strong>${value.name}</strong><br>`;
                    tooltipContent += `${value.componentName}<br>`;
                    tooltipContent += `${this.$t('forms.labels.date')}: ${value.formattedDate}<br>`;
                    tooltipContent += `${this.$t('graphics.labels.value')}: ${value.yValue}<br>`;
                    tooltipContent += `${this.$t('graphics.labels.min')}: ${value.normMin}<br>`;
                    tooltipContent += `${this.$t('graphics.labels.max')}: ${value.normMax}<br>`;
                    tooltipContent += '</div>';
                });
                const chartContainerRect = this.$refs.chartContainer.getBoundingClientRect();
                const mouseEvent = event.sourceEvent || event;
                const relativeX = mouseEvent.clientX - chartContainerRect.left;
                const relativeY = mouseEvent.clientY - chartContainerRect.top;

                this.tooltipContent = tooltipContent;
                this.tooltipPosition = {
                    x: relativeX + 15,
                    y: relativeY - 28,
                };
                this.showTooltip = true;
            } else {
                this.showTooltip = false;
            }
        },
        zoomed(event) {
            const { transform } = event;
            this.currentTransform = transform;
            const newX = transform.rescaleX(this.x);
            const newYScales = this.yScales.map(yScale => transform.rescaleY(yScale));

            const { interval, format } = this.getTickSettings(newX);

            this.xAxisGroup.call(d3.axisBottom(newX).ticks(interval).tickFormat(format));
            // Rotate x-axis tick labels vertically after pan/zoom
            this.xAxisGroup.selectAll('text')
                .attr('transform', 'rotate(-90)')
                .style('text-anchor', 'end')
                .attr('dx', '-0.8em')
                .attr('dy', '-0.4em');
            this.applyXAxisStyles();

            this.xGridGroup.call(
                d3.axisBottom(newX)
                    .ticks(interval)
                    .tickSize(-this.chartHeight)
                    .tickFormat(''),
            );
            this.applyGridStyles(this.xGridGroup);

            this.yAxisGroups.forEach((yAxisGroup, index) => {
                const yAxis = index % 2 === 0 ? d3.axisLeft(newYScales[index]) : d3.axisRight(newYScales[index]);
                yAxisGroup.call(yAxis);

                yAxisGroup.select('.domain').remove();
                yAxisGroup
                    .selectAll('.tick line')
                    .attr('stroke', this.colorScale(index))
                    .attr('stroke-width', 1)
                    .attr('opacity', 0.8);
                yAxisGroup
                    .selectAll('.tick text')
                    .attr('fill', this.colorScale(index))
                    .style('font-size', '10px')
                    .style('font-weight', 'bold');
            });

            this.yGridGroup.call(
                d3.axisLeft(newYScales[0])
                    .ticks(10)
                    .tickSize(-this.chartWidth)
                    .tickFormat('')
                    .tickPadding(10),
            );
            this.applyGridStyles(this.yGridGroup);

            this.lineGroup
                .selectAll('.line')
                .attr('d', (dataArray, index) => {
                    const lineGenerator = d3
                        .line()
                        .x(dataPoint => newX(dataPoint.date))
                        .y(dataPoint => newYScales[index](dataPoint.value));
                    return lineGenerator(dataArray);
                });
            this.toggleMinMaxLines(this.showMinMaxLines, newYScales);
        },
        updateChart(event) {
            const extent = event.selection;
            if (!extent) {
                if (!this.idleTimeout) {
                    this.idleTimeout = setTimeout(() => {
                        this.idleTimeout = null;
                    }, 350);
                    return;
                }
                this.resetZoom();
            } else {
                this.x.domain([this.x.invert(extent[0][0]), this.x.invert(extent[1][0])]);
                this.yScales = this.yScales.map(yScale => yScale
                    .domain([yScale.invert(extent[1][1]), yScale.invert(extent[0][1]),
                    ]));
                this.lineGroup.select('.brush').call(this.brush.move, null);
                const { interval, format } = this.getTickSettings();

                this.xAxisGroup
                    .transition()
                    .duration(this.transitionTime)
                    .call(d3.axisBottom(this.x).ticks(interval).tickFormat(format));
                this.applyXAxisStyles();

                this.xGridGroup
                    .transition()
                    .duration(this.transitionTime)
                    .call(
                        d3.axisBottom(this.x)
                            .ticks(interval)
                            .tickSize(-this.chartHeight)
                            .tickFormat(''),
                    );
                this.applyGridStyles(this.xGridGroup);

                this.yAxisGroups.forEach((yAxisGroup, index) => {
                    const yAxis = index % 2 === 0
                        ? d3.axisLeft(this.yScales[index])
                        : d3.axisRight(this.yScales[index]);
                    yAxisGroup.transition().duration(this.transitionTime).call(yAxis);
                    yAxisGroup.select('.domain').remove();
                    yAxisGroup
                        .selectAll('.tick line')
                        .attr('stroke', this.colorScale(index))
                        .attr('stroke-width', 1)
                        .attr('opacity', 0.8);
                    yAxisGroup
                        .selectAll('.tick text')
                        .attr('fill', this.colorScale(index))
                        .style('font-size', '10px')
                        .style('font-weight', 'bold');
                });

                this.yGridGroup
                    .transition()
                    .duration(this.transitionTime)
                    .call(
                        d3.axisLeft(this.yScales[0])
                            .ticks(10)
                            .tickSize(-this.chartWidth)
                            .tickFormat(''),
                    );
                this.applyGridStyles(this.yGridGroup);

                this.lineGenerators = this.lineGenerators.map((_, index) => d3
                    .line()
                    .x(d => this.x(d.date))
                    .y(d => this.yScales[index](d.value)));

                this.lineGroup
                    .selectAll('.line')
                    .transition()
                    .duration(this.transitionTime)
                    .attr('d', (d, index) => this.lineGenerators[index](d));

                this.currentTransform = d3.zoomIdentity;
                this.toggleMinMaxLines(this.showMinMaxLines);
            }
        },
        updateLineVisibility() {
            this.lineGroup
                .selectAll('.line')
                .style('display', (d, index) => (this.lineVisibility[index] ? null : 'none'));
            this.yAxisGroups.forEach((yAxisGroup, index) => {
                yAxisGroup.style('display', this.lineVisibility[index] ? null : 'none');
            });
            this.minMaxValues = this.dataSeries.map((series, index) => {
                if (!this.lineVisibility[index]) return null;
                return {
                    minValue: d3.min(series, d => d.value),
                    maxValue: d3.max(series, d => d.value),
                    unit: series[0]?.metadata?.unit || '',
                };
            });
            this.toggleMinMaxLines(this.showMinMaxLines);
        },
        resetZoom() {
            const allDates = [].concat(
                ...this.dataSeries.map(series => series.map(d => d.date)),
            );
            this.x.domain(d3.extent(allDates));

            this.yScales = this.dataSeries.map(series => d3
                .scaleLinear()
                .domain([d3.min(series, d => d.value) - d3.max(series, d => d.value) * 0.1,
                    d3.max(series, d => d.value) + d3.max(series, d => d.value) * 0.1])
                .range([this.chartHeight, 0]));

            const { interval, format } = this.getTickSettings();

            this.xAxisGroup
                .transition()
                .call(d3.axisBottom(this.x).ticks(interval).tickFormat(format));
            this.applyXAxisStyles();

            this.xGridGroup
                .transition()
                .call(
                    d3.axisBottom(this.x)
                        .ticks(interval)
                        .tickSize(-this.chartHeight)
                        .tickFormat(''),
                );
            this.applyGridStyles(this.xGridGroup);

            this.yAxisGroups.forEach((yAxisGroup, index) => {
                const yAxis = index % 2 === 0
                    ? d3.axisLeft(this.yScales[index])
                    : d3.axisRight(this.yScales[index]);
                yAxisGroup.transition().call(yAxis);
                yAxisGroup.select('.domain').remove();
                yAxisGroup
                    .selectAll('.tick line')
                    .attr('stroke', this.colorScale(index))
                    .attr('stroke-width', 1)
                    .attr('opacity', 0.8);
                yAxisGroup
                    .selectAll('.tick text')
                    .attr('fill', this.colorScale(index))
                    .style('font-size', '10px')
                    .style('font-weight', 'bold');
            });

            this.yGridGroup
                .transition()
                .call(
                    d3.axisLeft(this.yScales[0])
                        .ticks(10)
                        .tickSize(-this.chartWidth)
                        .tickFormat(''),
                );
            this.applyGridStyles(this.yGridGroup);

            this.lineGenerators = this.lineGenerators.map((_, index) => d3
                .line()
                .x(d => this.x(d.date))
                .y(d => this.yScales[index](d.value)));

            this.lineGroup
                .selectAll('.line')
                .transition()
                .attr('d', (d, index) => this.lineGenerators[index](d));

            if (this.mode === 'pan') {
                this.svg
                    .transition()
                    .duration(750)
                    .call(this.zoom.transform, d3.zoomIdentity);
            }
            this.toggleMinMaxLines(this.showMinMaxLines);
        },
        applyXAxisStyles() {
            this.xAxisGroup
                .selectAll('text')
                .attr('dy', '0.45em')
                .style('font-size', 12)
                .style('fill', '#2a3e53');
        },
        applyGridStyles(gridGroup) {
            gridGroup
                .selectAll('line')
                .style('stroke', '#e0e0e0')
                .style('stroke-opacity', 0.7)
                .style('shape-rendering', 'crispEdges');

            gridGroup.selectAll('path').remove();
        },
        getTickSettings(newXScale) {
            const xDomain = newXScale ? newXScale.domain() : this.x.domain();
            const [start, end] = xDomain;
            const timeSpan = end - start;

            const setting = this.tickSettings.find(set => timeSpan > set.threshold);

            if (setting) {
                return {
                    interval: setting.tickInterval,
                    format: setting.tickFormat,
                };
            }

            const lastSetting = this.tickSettings[this.tickSettings.length - 1];
            return {
                interval: lastSetting.tickInterval,
                format: lastSetting.tickFormat,
            };
        },
        onUpdateStatMode() {
            this.$emit('updateStatMode', this.statMode);
        },

    },
};
</script>

  <style scoped>
  .toggle-button {
    padding: 5px 10px;
    font-size: 16px;
    cursor: pointer;
    border: none;
    color: #fff;
    background-color: #26384b;
    border-radius: 50px;
    text-wrap: nowrap;
  }

  .toggle-button.active {
    background-color: #007bff;
    color: white;
  }

  .line-toggles {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .line-toggles label {
    margin-right: 5px;
    cursor: pointer;
    padding: 5px;
  }

  .line {
    fill: none;
  }

  .brush .selection {
    fill: #777;
    fill-opacity: 0.3;
    stroke: #fff;
    shape-rendering: crispEdges;
  }

  .vertical-line {
    stroke: black;
    stroke-width: 1;
    stroke-dasharray: 3, 3;
  }

  .button-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    font-weight: 600;
    height: 20%;
    margin-bottom: 20px;
  }
  .button-group-left {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
  }
  .button-group-left > * {
    min-width: 0;
    flex-shrink: 1;
  }

  @media (max-width: 600px) {
    .button-group-left {
      gap: 4px;
    }
    .graphic-title {
      font-size: 1.1rem;
      padding: 2px 8px;
    }
  }

  .graphic-title {
    font-size: 1.5rem;
    font-weight: bold;
    background-color: #26384b;
    color: #fff;
    text-align: center;
    margin: 30px 0px;
    padding: 10px 16px;
  }

  .button-container .item:last-child {
    border: #c2c2c2;
    border-radius: 5px;
    border-style: solid;
    border-width: 2px;
    padding: 5px 10px;
    font-size: 16px;
    margin-left: auto;
  }

  .button-container-dashboard {
    padding: 5px 10px;
    font-size: 5px;
    margin-left: auto;
  }

  .y-axis .domain {
    display: none;
  }

  .y-axis .tick line {
    stroke-width: 1;
    opacity: 0.8;
  }
  .annotation-group:focus {
    outline: none;
  }

  .y-axis .tick text {
    font-size: 10px;
    font-weight: bold;
  }

  .grid line {
    stroke: #e0e0e0;
    stroke-opacity: 0.7;
    shape-rendering: crispEdges;
  }

  .grid path {
    display: none;
  }
  .checkbox-style{
    accent-color: black;
    margin-right: 5px;
  }

  .toggle-btn {
    transition: transform 0.3s ease-in-out;
  }

  .buttons-wrapper {
    display: flex;
    transition: width 0.3s ease-in-out;
    width: 0;
    align-items: center;
  }

  .buttons-wrapper.expanded {
    width: 10%;
  }

  .v-btn {
    margin-left: 8px;
  }
  .chart-container-dashboard {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.button-container {
  flex: 0 0 auto;
}

.graphicsWrapper {
  flex: 1 1 auto;
  overflow: hidden;
}
.chart-container-stat{
    height: 100%;
}

.spinner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 999;
  width: 100%;
  height: 100%;

  display: flex;
  justify-content: center;
  align-items: center;

  background-color: rgba(255, 255, 255, 0.7);
}

  </style>
